import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  adminTable,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const Container = styled.div`
  padding: 1.5em;
  background: transparent;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${({ colors }) => colors.buttonBackground};
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.9em;
  margin-bottom: 1em;
  &:hover {
    color: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const SectionTitle = styled.h1`
  margin: 0.5em 0;
  font-size: 2em;
  color: ${({ colors }) => colors.labelText};
`;

const DefinitionList = styled.dl`
  margin: 0 0 1em 0;
  dt {
    font-weight: bold;
    margin-top: 0.5em;
  }
  dd {
    margin: 0.2em 0;
  }
  .pending {
    color: #e09723;
    font-size: 0.85em;
    margin-left: 0.5em;
  }
`;

const LinkButton = styled.button`
  display: inline-block;
  margin: 0.5em 0 1.5em 0;
  padding: 0.4em 0.8em;
  background: ${({ colors }) => colors.buttonBackground};
  color: white;
  border-radius: 4px;
  font-size: 0.9em;
  cursor: pointer;
  border: none;
  &:hover {
    background: ${({ colors }) => colors.buttonHoverBackground};
  }
`;

const Table = styled.table`
  ${adminTable};
  margin-top: 1em;
  font-size: 0.9em;

  thead {
    display: none;
  }

  tbody td,
  tbody th {
    ${adminTableDataCell};
    padding: 8px;
    border: 1px solid #ccc;
    vertical-align: top;
  }

  tbody th {
    ${adminTableDataCell};
    background: #f9f9f9;
    font-weight: 600;
  }
`;

export default function SubscriptionPOCDetailContent({ colors, onBack }) {
  const navigate = useNavigate();
  const sub = useSelector((s) => s.auth.selected_subscriptions) || {};
  const invoiceDetails =
    useSelector((s) => s.auth.subscription_details?.invoiceDetail) || [];
  const poc = useSelector((s) => s.auth.subscription_details?.customerDetail) || {};

  const normalizeId = (value) =>
    value === undefined || value === null ? "" : String(value).trim();
  const hasValue = (value) => value !== undefined && value !== null && value !== "";
  const pick = (...values) => values.find(hasValue);
  const show = (...values) => {
    const value = pick(...values);
    return hasValue(value) ? value : "—";
  };

  const matchingDetail = useMemo(() => {
    if (!Array.isArray(invoiceDetails) || invoiceDetails.length === 0) {
      return null;
    }

    const selectedInvoiceId = normalizeId(sub.invoice_id);
    const selectedInvoiceNumber = normalizeId(sub.invoice_number);
    const selectedCustomerId = normalizeId(sub.customer_id);
    const selectedCustomerCode = normalizeId(sub.customer_code);

    return (
      invoiceDetails.find((detail) => {
        const detailInvoiceId = normalizeId(detail?.invoice_id);
        const detailInvoiceNumber = normalizeId(detail?.invoice_number);
        const detailCustomerId = normalizeId(detail?.customer_id);
        const detailCustomerCode = normalizeId(detail?.customer_code);

        const invoiceMatches =
          (selectedInvoiceId && detailInvoiceId === selectedInvoiceId) ||
          (selectedInvoiceNumber && detailInvoiceNumber === selectedInvoiceNumber);
        const customerMatches =
          (selectedCustomerId && detailCustomerId === selectedCustomerId) ||
          (selectedCustomerCode && detailCustomerCode === selectedCustomerCode);

        return invoiceMatches && customerMatches;
      }) ||
      invoiceDetails.find(
        (detail) =>
          normalizeId(detail?.invoice_id) === selectedInvoiceId ||
          normalizeId(detail?.invoice_number) === selectedInvoiceNumber
      ) ||
      null
    );
  }, [invoiceDetails, sub.customer_code, sub.customer_id, sub.invoice_id, sub.invoice_number]);

  const detail = matchingDetail || {};

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${month}-${day}-${year}`;
  };

  // confirmation modal state for Update
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);

  // Decide whether to use change_* fields or original ones
  const name = poc.change_poc_name || poc.contact_name || "—";
  const email = poc.change_poc_email || poc.contact_email;
  const phone = poc.change_poc_phone || poc.contact_phone;
  const ext = poc.change_poc_extension || poc.contact_extension;
  const namePending = Boolean(poc.change_poc_name);
  const emailPending = Boolean(poc.change_poc_email);
  const phonePending = Boolean(poc.change_poc_phone);

  // Build address lines
  const addressLines = [];
  const orgStreet = pick(detail.org_street, sub.org_street);
  const orgCity = pick(detail.org_city, sub.org_city);
  const orgState = pick(detail.org_state, sub.org_state);
  const orgZip = pick(detail.org_zip, sub.org_zip);
  const orgCountry = pick(detail.org_country, sub.org_country);

  if (orgStreet) addressLines.push(orgStreet);
  if (orgCity || orgState || orgZip) {
    const csz = [orgCity, orgState, orgZip].filter(Boolean).join(", ");
    addressLines.push(csz);
  }
  if (orgCountry) addressLines.push(orgCountry);

  const handleUpdateConfirm = () => {
    setConfirmUpdateOpen(false);
    navigate("/admincentral/subscriptions", {
      state: { defaultTab: "Point of Contact" }
    });
  };

  return (
    <Container>
      <BackLink colors={colors} onClick={onBack}>
        &lt;&lt; Back
      </BackLink>

      <SectionTitle colors={colors}>Point of Contact</SectionTitle>
      <DefinitionList>
        <dt>
          {name}
          {namePending && <span className="pending">(Pending Approval)</span>}
        </dt>
        <dd>&nbsp;</dd>
        <dt>
          {email
            ? <>
                <a href={`mailto:${email}`}>{email}</a>
                {emailPending && <span className="pending">(Pending Approval)</span>}
              </>
            : "—"}
        </dt>
        <dd>&nbsp;</dd>
        <dt>
          {phone
            ? <>
                <a href={`tel:${phone.replace(/[^\d]/g, "")}`}>
                  {phone}{ext ? ` x${ext}` : ""}
                </a>
                {phonePending && <span className="pending">(Pending Approval)</span>}
              </>
            : "—"}
        </dt>
      </DefinitionList>

      <LinkButton colors={colors} onClick={handleUpdateConfirm}>
        Update
      </LinkButton>

      <SectionTitle colors={colors}>Subscription Details</SectionTitle>
      <Table>
        <tbody>
          <tr>
            <td>Customer ID</td>
            <td>{show(detail.customer_code, sub.customer_code, detail.customer_id, sub.customer_id)}</td>
          </tr>
          <tr>
            <td>Invoice Number</td>
            <td>{show(detail.invoice_number, sub.invoice_number, detail.invoice_id, sub.invoice_id)}</td>
          </tr>
          <tr>
            <td>Item Number</td>
            <td>{show(detail.item_number, sub.item_number)}</td>
          </tr>
          <tr>
            <td>Purchase Date</td>
            <td>{formatDate(pick(detail.purchase_date, sub.purchase_date))}</td>
          </tr>
          <tr>
            <td>Address</td>
            <td>
              {addressLines.length
                ? addressLines.map((line, idx) => <div key={idx}>{line}</div>)
                : "—"}
            </td>
          </tr>
          <tr>
            <td>Start Date</td>
            <td>{formatDate(pick(detail.start_date, sub.start_date))}</td>
          </tr>
          <tr>
            <td>End Date</td>
            <td>{formatDate(pick(detail.end_date, sub.end_date))}</td>
          </tr>
          <tr>
            <td>Product Code</td>
            <td>{show(detail.product_code, sub.product_code)}</td>
          </tr>
          <tr>
            <td>Subscription</td>
            <td>{show(detail.product_desc, sub.product_desc, detail.item_description, sub.item_description)}</td>
          </tr>
          <tr>
            <td>Quantity</td>
            <td>{show(detail.quantity, sub.quantity)}</td>
          </tr>
          <tr>
            <td>Duration</td>
            <td>{show(detail.duration, sub.duration)}</td>
          </tr>
          <tr>
            <td>Number of Schools</td>
            <td>{show(detail.number_of_schools, sub.number_of_schools)}</td>
          </tr>
          <tr>
            <td>Invoice Amount</td>
            <td>
              {hasValue(pick(detail.invoice_amount, sub.invoice_amount))
                ? `$${pick(detail.invoice_amount, sub.invoice_amount)}`
                : "—"}
            </td>
          </tr>
        </tbody>
      </Table>
    </Container>
  );
}
